import { useMemo, useState } from "react";

import useIsomorphicLayoutEffect from "@app/common/hooks/useIsomorphicLayoutEffect";

export type UseMeasureRect = Pick<
  DOMRectReadOnly,
  "x" | "y" | "top" | "left" | "right" | "bottom" | "height" | "width"
>;
export type UseMeasureRef<E extends Element = Element> = (
  element: E | null
) => void;
export type UseMeasureResult<E extends Element = Element> = [
  UseMeasureRef<E>,
  UseMeasureRect,
  { innerWidth: number; innerHeight: number },
];

const DEFAULT_STATE: UseMeasureRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

const useMeasure = <E extends Element = Element>(): UseMeasureResult<E> => {
  const [element, ref] = useState<E | null>(null);
  const [rect, setRect] = useState<UseMeasureRect>(DEFAULT_STATE);

  const observer = useMemo(
    () =>
      new ResizeObserver((entries) => {
        if (entries[0]) {
          const { x, y, width, height, top, left, bottom, right } =
            entries[0].contentRect;
          setRect({ x, y, width, height, top, left, bottom, right });
        }
      }),
    []
  );

  useIsomorphicLayoutEffect(() => {
    if (!element) return;
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  const innerWidth = element
    ? element.clientWidth -
      parseFloat(getComputedStyle(element).paddingLeft) -
      parseFloat(getComputedStyle(element).paddingRight)
    : 0;

  const innerHeight = element
    ? element.clientHeight -
      parseFloat(getComputedStyle(element).paddingTop) -
      parseFloat(getComputedStyle(element).paddingBottom)
    : 0;

  // Return client height and width as soon as possible
  const returnRect =
    rect === DEFAULT_STATE
      ? {
          ...DEFAULT_STATE,
          width: element?.clientWidth ?? 0,
          height: element?.clientHeight ?? 0,
        }
      : rect;

  return [ref, returnRect, { innerWidth, innerHeight }];
};

export default useMeasure;
